import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Lock, Clock, Key, Save } from 'lucide-react';
import { toast } from 'sonner';

const TIMEOUT_OPTIONS = [
  { value: '1', label: '1 minute' },
  { value: '2', label: '2 minutes' },
  { value: '5', label: '5 minutes' },
  { value: '10', label: '10 minutes' },
  { value: '15', label: '15 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '60', label: '1 hour' },
  { value: '0', label: 'Never (disabled)' },
];

export default function POSSettings() {
  const [settings, setSettings] = useState({
    lock_timeout_minutes: 5,
    require_pin: true
  });
  const [hasPin, setHasPin] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
    checkHasPin();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/pos-settings');
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load POS settings');
    } finally {
      setLoading(false);
    }
  };

  const checkHasPin = async () => {
    try {
      const response = await api.get('/pos/has-pin');
      setHasPin(response.data.has_pin);
    } catch (error) {
      console.error('Failed to check PIN status');
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/pos-settings', settings);
      toast.success('POS settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSetPin = async () => {
    if (!newPin || newPin.length < 4 || newPin.length > 6) {
      toast.error('PIN must be 4-6 digits');
      return;
    }
    if (newPin !== confirmPin) {
      toast.error('PINs do not match');
      return;
    }
    if (!/^\d+$/.test(newPin)) {
      toast.error('PIN must contain only digits');
      return;
    }

    try {
      await api.post('/pos/set-pin', { pin: newPin });
      toast.success('PIN set successfully');
      setHasPin(true);
      setNewPin('');
      setConfirmPin('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to set PIN'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-10 w-10 rounded-full border-4 border-orange-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="pos-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>POS Settings</h1>
        <p className="text-gray-500 text-sm mt-1">Configure POS lock screen and security options</p>
      </div>

      {/* Lock Screen Settings */}
      <Card className="border-orange-100 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-800">
            <Lock className="h-5 w-5 text-orange-500" />
            Lock Screen Settings
          </CardTitle>
          <CardDescription>Configure automatic lock timeout and security requirements</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-gray-800">Require PIN for unlock</Label>
                <p className="text-sm text-gray-500">Users can unlock with PIN or password</p>
              </div>
              <Switch
                checked={settings.require_pin}
                onCheckedChange={(checked) => setSettings({ ...settings, require_pin: checked })}
                data-testid="require-pin-toggle"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-500" />
                <Label className="text-gray-800">Auto-lock timeout</Label>
              </div>
              <Select 
                value={String(settings.lock_timeout_minutes)} 
                onValueChange={(v) => setSettings({ ...settings, lock_timeout_minutes: parseInt(v) })}
              >
                <SelectTrigger className="w-[200px] border-orange-200" data-testid="lock-timeout-select">
                  <SelectValue placeholder="Select timeout" />
                </SelectTrigger>
                <SelectContent>
                  {TIMEOUT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500">
                {settings.lock_timeout_minutes === 0 
                  ? 'POS will never auto-lock (not recommended)'
                  : `POS will lock after ${settings.lock_timeout_minutes} minute${settings.lock_timeout_minutes > 1 ? 's' : ''} of inactivity`
                }
              </p>
            </div>
          </div>

          <Button 
            onClick={handleSaveSettings} 
            disabled={saving}
            className="bg-gradient-orange hover:opacity-90 text-white btn-glow"
            data-testid="save-settings-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </CardContent>
      </Card>

      {/* PIN Management */}
      <Card className="border-purple-100 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-800">
            <Key className="h-5 w-5 text-purple-500" />
            Your POS PIN
          </CardTitle>
          <CardDescription>
            {hasPin ? 'You have a PIN set. You can change it below.' : 'Set a PIN for quick POS unlock'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
            <p className="text-sm text-gray-600 mb-4">
              Your PIN is used to quickly unlock the POS screen. It must be 4-6 digits.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{hasPin ? 'New PIN' : 'Enter PIN'}</Label>
                <Input
                  type="password"
                  placeholder="••••"
                  maxLength={6}
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                  className="border-purple-200 focus:border-purple-400"
                  data-testid="new-pin-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Confirm PIN</Label>
                <Input
                  type="password"
                  placeholder="••••"
                  maxLength={6}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                  className="border-purple-200 focus:border-purple-400"
                  data-testid="confirm-pin-input"
                />
              </div>
            </div>
          </div>
          <Button 
            onClick={handleSetPin}
            className="bg-gradient-purple hover:opacity-90 text-white btn-glow-purple"
            data-testid="set-pin-btn"
          >
            <Key className="h-4 w-4 mr-2" />
            {hasPin ? 'Update PIN' : 'Set PIN'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
